import { inject, Injectable } from "@angular/core";
import { MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductService } from "@domain/product/product.service";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { isFgdsProject } from "@domain/project/project-guards";
import { User } from "@domain/user/user";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { FloorplanWorkspace } from "@project/floorplanner/floorplan-workspace";
import { ProductConfigurationService } from "@project/product-configuration.service";
import { combineLatest, forkJoin, map, Observable, of, switchMap, take, tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class FloorplanWorkspaceWarningSignService {
  private productService = inject(ProductService);
  private monitoringProductService = inject(MonitoringProductService);
  private userService = inject(UserService);
  private productConfigurationService = inject(ProductConfigurationService);

  public updateWarningSigns(
    floorplan: Floorplan,
    floorplanWorkspace: FloorplanWorkspace,
    extraIncompleteConfigIds$?: Observable<string[]>,
  ): Observable<void> {
    return this.userService.user$.pipe(
      take(1),
      map((user: User) => user.scope),
      switchMap((scope) => {
        const allProductIds$ =
          scope === BusinessScope.FGDS
            ? this.productService.getAllProductIds()
            : this.monitoringProductService.getAllProductMatNrs();
        const discontinuedProductIds$ =
          scope === BusinessScope.FGDS
            ? this.productService.getDiscontinuedProductIds()
            : this.monitoringProductService.getDiscontinuedMatNrs(this.productService.getFilterCountry());

        return forkJoin([allProductIds$, discontinuedProductIds$]).pipe(
          switchMap(([allProductIds, discontinuedProductIds]) => {
            this.updatePlaceholderWarningSigns(floorplan, floorplanWorkspace, allProductIds);
            return this.updateProductWarningSigns(
              floorplan,
              floorplanWorkspace,
              discontinuedProductIds,
              extraIncompleteConfigIds$,
            );
          }),
          map(() => undefined),
        );
      }),
    );
  }

  private updateProductWarningSigns(
    floorplan: Floorplan,
    floorplanWorkspace: FloorplanWorkspace,
    discontinuedProductIds: string[],
    extraIncompleteConfigIds$?: Observable<string[]>,
  ): Observable<void> {
    const discontinuedConfigIds = floorplan.project.productConfigurations
      .filter((config) =>
        config.configuredProductIds.some((configuredProductId) => discontinuedProductIds.includes(configuredProductId)),
      )
      .map((config) => config.id);

    const project = floorplan.project;
    const backendIncompleteIds$ = isFgdsProject(project)
      ? this.productConfigurationService.getIncompleteConfigIds(project)
      : of<string[]>([]);
    const localIncompleteIds$ = extraIncompleteConfigIds$ ?? of([]);

    return combineLatest([backendIncompleteIds$, localIncompleteIds$]).pipe(
      take(1),
      tap(([backendIncompleteIds, localIncompleteIds]) => {
        const union = Array.from(new Set([...(backendIncompleteIds ?? []), ...(localIncompleteIds ?? [])]));
        const configIdsThatNeedWarningSign = discontinuedConfigIds.concat(union);
        [
          floorplan.plasticSigns,
          floorplan.signalElements,
          floorplan.transmitters,
          floorplan.flameDetectors,
          floorplan.gasWarningCenters,
          floorplan.alarmDevices,
          floorplan.patientMonitors,
          floorplan.centralStations,
          floorplan.telemetries,
          floorplan.kvms,
          floorplan.displays,
          floorplan.utilities,
        ]
          .flat()
          .forEach((item) => {
            floorplanWorkspace.refreshWarningSignVisibility(item, configIdsThatNeedWarningSign.includes(item.config.id));
          });
      }),
      map(() => undefined),
    );
  }

  private updatePlaceholderWarningSigns(floorplan: Floorplan, floorplanWorkspace: FloorplanWorkspace, allProductIds: string[]) {
    [
      floorplan.transmitterPlaceholders,
      floorplan.flameDetectorPlaceholders,
      floorplan.gasWarningCenterPlaceholders,
      floorplan.alarmDevicePlaceholders,
      floorplan.plasticSignPlaceholders,
      floorplan.signalElementPlaceholders,
      floorplan.patientMonitorPlaceholders,
      floorplan.centralStationPlaceholders,
      floorplan.telemetryPlaceholders,
      floorplan.kvmPlaceholders,
      floorplan.displayPlaceholders,
      floorplan.utilityPlaceholders,
    ]
      .flat()
      .forEach((item) => {
        return floorplanWorkspace.refreshWarningSignVisibility(
          item,
          item.products.length > 0 && item.products.every((product: PlaceholderProduct) => allProductIds.includes(product.id)),
        );
      });
  }
}
